import { Component, Inject } from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { CorporateDirectoryUser } from 'src/app/corporate-directory/models/corporate-directory-user.model';
import { AssignTravelXTeamByCandidate } from 'src/app/travelxnew/models/assign-travelx-team-by-candidate.model';
import { TravelXNewService } from 'src/app/travelxnew/travelxnew.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'nova-assign-travelx-team-by-candidate-dialog',
    templateUrl: './assign-travelx-team-by-candidate-dialog.component.html',
    styleUrls: ['./assign-travelx-team-by-candidate-dialog.component.scss']
})
export class AssignTravelXTeamByCandidateDialogComponent {
    readonly candidateTextMap: { [k: string]: string } = {
        '=1': 'Candidate',
        other: '# Candidates'
    };

    candidateIds: number[] = [];
    isSaving = false;
    managersLoading = false;
    specialistsLoading = false;

    manager: CorporateDirectoryUser;
    specialist: CorporateDirectoryUser;

    managers$: Observable<CorporateDirectoryUser[]>;
    specialists$: Observable<CorporateDirectoryUser[]>;

    get canSubmit(): boolean {
        return !!this.manager || !!this.specialist;
    }

    constructor(
        private readonly _matDialogRef: MatDialogRef<AssignTravelXTeamByCandidateDialogComponent>,
        private readonly _travelXNewService: TravelXNewService,
        @Inject(MAT_DIALOG_DATA)
        private readonly _data: { candidateIds: number[] }
    ) {
        this.candidateIds = _data.candidateIds || [];
    }

    employeeDisplay(employee: CorporateDirectoryUser): string {
        if (employee.title) {
            return `${employee.name} (${employee.title})`.trim();
        }
        return employee.name.trim();
    }

    selectEmployee(event: MatAutocompleteSelectedEvent, fieldName: string): void {
        switch (fieldName) {
            case 'manager':
                this.manager = event.option.value as CorporateDirectoryUser;
                break;
            case 'specialist':
                this.specialist = event.option.value as CorporateDirectoryUser;
                break;
        }
    }

    listEmployees(searchText: string, fieldName: string): void {
        switch (fieldName) {
            case 'manager':
                this.managersLoading = true;
                this.managers$ = of(searchText).pipe(
                    debounceTime(500),
                    switchMap((value: string) => this._travelXNewService.getTravelXPersonnel(value)),
                    tap(() => (this.managersLoading = false))
                );
                break;
            case 'specialist':
                this.specialistsLoading = true;
                this.specialists$ = of(searchText).pipe(
                    debounceTime(500),
                    switchMap((value: string) => this._travelXNewService.getTravelXPersonnel(value)),
                    tap(() => (this.specialistsLoading = false))
                );
                break;
        }
    }

    clearEmployee(fieldName: string): void {
        switch (fieldName) {
            case 'manager':
                this.manager = undefined;
                break;
            case 'specialist':
                this.specialist = undefined;
                break;
        }
    }

    assignByCandidate(): void {
        const saveModel: AssignTravelXTeamByCandidate = {
            candidateIds: this.candidateIds,
            managerId: this.manager?.userId,
            specialistId: this.specialist?.userId
        };
        this.isSaving = true;
        this._travelXNewService
            .assignByCandidate(saveModel)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._matDialogRef.close(true),
                error: () => {
                    this.isSaving = false;
                }
            });
    }
}
